import {t} from "@lingui/core/macro";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {RequestInfo} from "@khan/shared-core";

import StoreBadge from "./store-badge.tsx";

type AppStoreBadgeProps = {
    safeAppDownloadUrl: string;
    /**
     * Set to true if the badge will be displayed on a dark background.
     * Adjusts features like the color of the focus ring.
     */
    light?: boolean;
    style?: StyleType;
};

/**
 * Maps the locale codes used by Khan Academy to the locale codes used
 * by Apple’s badge API. This is effectively the intersection of locales
 * supported by Khan Academy and locales supported by Apple.
 *
 * @see {@link https://tools.applemediaservices.com/app/469863705|Badges and Lockups}
 *      for the list of locales Apple localizes their badge to.
 *
 * To find Apple’s locale code use the badge generator to generate an
 * image for your target locale. Then inspect the HTML markup of the
 * displayed badge image. The locale code appears in the <img>’s `src`
 * attribute.
 *
 * Alternatively, you can press “Copy Embed” then paste the generated
 * HTML markup somewhere and inspect the `src` attribute that way.
 */
const KA_LOCALE_TO_BADGE_LOCALE = {
    ar: "ar-ar",
    az: "az-tr",
    bg: "bg-bg",
    cs: "cs-cz",
    da: "da-dk",
    de: "de-de",
    el: "el-gr",
    en: "en-us",
    es: "es-es",
    fr: "fr-fr",
    he: "hb-hb",
    hu: "hu-hu",
    id: "id-id",
    it: "it-it",
    ja: "ja-jp",
    ko: "ko-kr",
    lt: "lt-lt",
    lv: "lv-lv",
    ms: "ms-my",
    nb: "no-no",
    nl: "nl-nl",
    pl: "pl-pl",
    pt: "pt-br",
    "pt-pt": "pt-pt",
    ro: "ro-ro",
    ru: "ru-ru",
    sk: "sk-sk",
    sl: "sl-si",
    sv: "sv-se",
    th: "th-th",
    tr: "tr-tr",
    vi: "vi-vn",
    "zh-hans": "zh-cn",
    "zh-hant": "zh-hk",
} as const;

/**
 * Renders a localized version of Apple’s “Download on the App Store”
 * badge. Uses the kaLocale of the request to choose an image. If the
 * locale is not recognized or not provided by Apple the badge will
 * fall back to U.S. English.
 */
export default function AppStoreBadge(
    props: AppStoreBadgeProps,
): React.ReactElement {
    const targetKaLocale = RequestInfo.kaLocale;

    const badgeLocale =
        KA_LOCALE_TO_BADGE_LOCALE[targetKaLocale] ||
        KA_LOCALE_TO_BADGE_LOCALE.en;

    const badgeUrl = `https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/${badgeLocale}?releaseDate=1331424000`;

    return (
        <StoreBadge
            safeAppDownloadUrl={props.safeAppDownloadUrl}
            badgeImageUrl={badgeUrl}
            altText={t`Download on the App Store`}
            light={props.light}
            style={props.style}
        />
    );
}
